/**
 * <TutorQualityAxisCard> — un axe de « Les bases » du bilan de séance (molecule).
 *
 * L'écran d'aujourd'hui (`ProxyCard` de `QualityClosureScreen`) : une carte par axe — présence,
 * connexion, environnement —, son glyphe, son nom et son état à droite : « OK » en vert,
 * « Attention » en orange, « Alerte » en rouge (un manquement à la charte). Le liseré rouge ne reste
 * qu'à la carte en « Alerte » : posé sur chaque carte comme dans le legacy, il ne distinguait aucune
 * d'elles ; sur la seule qui manque à la charte, il la détache de ses voisines (comme le liseré de
 * `<TutorSessionFeedCard>`, gardé sur une carte `action`). Un axe qui n'est pas OK écrit ses alertes dessous, ouvertes (`<TutorQualityAlerts>`).
 * La coche verte sur un axe OK est voulue : elle rassure le masteur même quand rien ne va mal.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { type SemanticColorKey, textInkOnFill } from "../../theme/tokens/colors"
import { Card } from "../Card"
import { TutorQualityAlerts } from "../TutorQualityAlerts"
import { type UITutorQualityAxis, type UITutorQualityStatus } from "../../../ports/primary/ui/types/tutorCloseOut"

/** Le visage d'un état : sa couleur, son glyphe, son mot — la table de l'écran d'aujourd'hui. */
export const QUALITY_STATUS: Record<UITutorQualityStatus, { color: SemanticColorKey; icon: IconName; label: string }> =
    {
        ok: { color: "success", icon: "check-circle", label: "OK" },
        warning: { color: "warning", icon: "exclamation-triangle", label: "Attention" },
        alert: { color: "danger", icon: "exclamation-circle", label: "Alerte" },
    }

export type TutorQualityAxisCardProps = {
    axis: UITutorQualityAxis
    testID?: string
}

export function TutorQualityAxisCard(props: TutorQualityAxisCardProps) {
    const { axis, testID } = props
    const face = QUALITY_STATUS[axis.status]
    const ink = textInkOnFill(face.color)

    return (
        <Card leftBorderColor={axis.status === "alert" ? face.color : undefined} p="4" testID={testID}>
            <Stack gap="3">
                <Row gap="2" align="center">
                    <Icon name={face.icon} size={20} color={ink} />
                    <Text size="text-md" weight="semibold" style={styles.name}>
                        {axis.name}
                    </Text>
                    <Text size="text-sm" weight="bold" color={ink}>
                        {face.label}
                    </Text>
                </Row>
                {axis.status !== "ok" && axis.alerts.length > 0 && <TutorQualityAlerts alerts={axis.alerts} />}
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    name: {
        flex: 1,
        flexShrink: 1,
    },
}))
